// Pure Highcharts options builder for the scatterplot widget. Shared by the
// widget's process() step and the configurator's live preview, so the preview
// renders exactly what the grid will.
// Set every option key in every state, and give every series an id.
// HighchartsReact calls chart.update(), which merges the new options into the old
// options, so an option that the builder omits keeps its old value. It also
// matches a series that has no id to the old series at the same index.

import type {
  ScatterplotBuilderMisc,
  ScatterplotComponentSpec,
  ScatterplotNonSeriesResponse,
  ScatterplotAxisResult,
  ScatterplotPoint,
  ScatterplotTrendline,
} from "../Processing/types";
import { formatSubjectNames } from "../Processing/seriesLabel";

const TRENDLINE_ALL_KEY = "__all__";
const TRENDLINE_NEUTRAL_COLOR = "#666666";
/** entity_key (and series_key) of the one-dot-per-day series under teamAverages. */
const SELECTION_ENTITY_KEY = "team";
const AGG_WORDS: Record<string, string> = { avg: "Average", sum: "Total", best: "Best" };

/** How the aggregation collapses the selection into one dot per day. xAgg is null in date mode. */
export function selectionAggWord(xAgg: string | null, yAgg: string): string {
  const word = (agg: string) => AGG_WORDS[agg] ?? agg;
  if (xAgg === null || xAgg === yAgg) return word(yAgg);
  return `${word(xAgg)} X / ${word(yAgg)} Y`;
}

/** Name of the one-dot-per-day series. The backend always sends "Team Average", which is
 *  wrong for a part of the roster and for a sum or best aggregation. */
export function selectionSeriesLabel(
  misc: Pick<ScatterplotBuilderMisc, "selectedSubjects" | "selectedSubjectNames">,
  aggWord: string,
): string {
  const subjects = misc.selectedSubjects ?? [];
  const names = (misc.selectedSubjectNames ?? []).slice(0, subjects.length);
  if (subjects.length === 0 || subjects.includes("full-team")) return `Team ${aggWord}`;
  if (names.length < subjects.length) return `Selection ${aggWord}`;
  const onlyPlayers = subjects.every((s) => s.startsWith("player-"));
  if (subjects.length === 1) return onlyPlayers ? names[0] : `${names[0]} ${aggWord}`;
  return onlyPlayers ? `Selection ${aggWord}: ${formatSubjectNames(names)}` : `Selection ${aggWord}`;
}

interface ScatterPointCustom {
  date: string;
  entityLabel: string;
  position?: string | null;
  gameday?: string | null;
  xLabel: string;
  yLabel: string;
}

/** Format one coordinate for the tooltip: a semantic backend label (e.g. a
 *  wellness answer text) wins; otherwise round to the axis decimals + unit. */
function formatAxisValue(value: number, backendLabel: string, axis: ScatterplotAxisResult): string {
  if (backendLabel && backendLabel !== String(value)) return backendLabel;
  const rounded = Number.isFinite(value) ? value.toFixed(Math.max(0, axis.decimals)) : String(value);
  return axis.unit ? `${rounded} ${axis.unit}` : rounded;
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-19" → "Oct 19" for a point label (Harman's call: this short form or the
 *  platform's YYYY-MM-DD, nothing else). Any other shape is returned as sent. */
export function formatPointDate(date: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(date);
  const month = match ? MONTH_ABBR[Number(match[1]) - 1] : undefined;
  return match && month ? `${month} ${Number(match[2])}` : date;
}

function axisTitle(axis: ScatterplotAxisResult): string {
  return axis.unit ? `${axis.label} (${axis.unit})` : axis.label;
}

/** Backend plot lines → Highcharts plotLines, matching the chart widget's
 *  custom-ref-line styling (ShortDash, width 2, zIndex 5, colored label). */
function toPlotLines(axis: ScatterplotAxisResult): Record<string, unknown>[] {
  return (axis.plot_lines ?? []).map((line) => {
    const color = line.colorHex || "#000000";
    const text = line.label || String(line.value);
    return {
      value: line.value,
      color,
      width: 2,
      dashStyle: "ShortDash",
      zIndex: 5,
      label: { text, style: { color, fontSize: "10px" } },
    };
  });
}

/** Soft extremes with 5% headroom so plot lines outside the data always render
 *  (Highcharts clips plot lines beyond the axis extremes). */
function softExtremes(axis: ScatterplotAxisResult): Record<string, unknown> {
  const values = (axis.plot_lines ?? []).map((l) => l.value);
  if (values.length === 0) return { softMin: null, softMax: null };
  const pads = values.map((v) => Math.abs(v) * 0.05);
  return {
    softMin: Math.min(...values.map((v, i) => v - pads[i])),
    softMax: Math.max(...values.map((v, i) => v + pads[i])),
  };
}

function buildScatterSeries(spec: ScatterplotComponentSpec, points: ScatterplotPoint[], selectionLabel: string) {
  const showPointLabels = spec.widgetMutations.showPointLabels === true;
  const showPointDates = showPointLabels && spec.widgetMutations.showPointDates === true;
  const seriesOrder: string[] = [];
  const bySeries = new Map<string, { name: string; color: string; data: Record<string, unknown>[] }>();

  for (const p of points) {
    let bucket = bySeries.get(p.series_key);
    if (!bucket) {
      const name = p.series_key === SELECTION_ENTITY_KEY ? selectionLabel : p.series_label;
      bucket = { name, color: p.hex, data: [] };
      bySeries.set(p.series_key, bucket);
      seriesOrder.push(p.series_key);
    }
    const custom: ScatterPointCustom = {
      date: p.date,
      entityLabel: p.entity_key === SELECTION_ENTITY_KEY ? selectionLabel : p.entity_label,
      position: p.position,
      gameday: p.gameday,
      xLabel: p.x_label,
      yLabel: p.y_label,
    };
    bucket.data.push({ x: p.x, y: p.y, custom });
  }

  return seriesOrder.map((key) => {
    const bucket = bySeries.get(key)!;
    return {
      type: "scatter",
      id: key,
      name: bucket.name,
      color: bucket.color,
      data: bucket.data,
      // Season-long player-day clouds exceed Highcharts' 1000-point turbo limit
      // for object points; 0 disables the limit.
      turboThreshold: 0,
      marker: { symbol: "circle", radius: 3.5 },
      dataLabels: {
        enabled: showPointLabels,
        allowOverlap: false,
        style: { fontSize: "10px", fontWeight: "400", textOutline: "1px #ffffff" },
        formatter(this: { point?: { options?: { custom?: ScatterPointCustom } } }): string {
          const custom = this.point?.options?.custom;
          if (!custom) return "";
          return showPointDates ? `${custom.entityLabel} (${formatPointDate(custom.date)})` : custom.entityLabel;
        },
      },
    };
  });
}

function buildTrendlineSeries(trendlines: ScatterplotTrendline[], scatterSeries: Array<{ id?: string; color?: string }>) {
  const colorBySeries = new Map(scatterSeries.map((s) => [s.id, s.color]));
  return trendlines.map((t) => {
    const isAll = t.series_key === TRENDLINE_ALL_KEY;
    return {
      type: "line",
      id: `trendline-${t.series_key}`,
      name: `Trendline (R² ${t.r_squared.toFixed(2)})`,
      color: isAll ? TRENDLINE_NEUTRAL_COLOR : colorBySeries.get(t.series_key) ?? TRENDLINE_NEUTRAL_COLOR,
      data: [
        { x: t.x0, y: t.y0 },
        { x: t.x1, y: t.y1 },
      ],
      dashStyle: "Dash",
      lineWidth: 2,
      marker: { enabled: false },
      enableMouseTracking: false,
      states: { hover: { lineWidthPlus: 0 } },
      zIndex: -1,
      // A per-series trendline toggles with its parent series and stays out of
      // the legend; the single all-points line gets its own legend entry.
      ...(isAll ? {} : { linkedTo: t.series_key, showInLegend: false }),
    };
  });
}

export function buildScatterplotOptions(
  spec: ScatterplotComponentSpec,
  data: ScatterplotNonSeriesResponse | null,
): Record<string, unknown> {
  const dateMode = spec.xAxisMode === "date";
  const xAxisResult: ScatterplotAxisResult = data?.x_axis ?? {
    mode: dateMode ? "date" : "metric",
    label: dateMode ? "Date" : (spec.xAxis?.displayNameOverride || spec.xAxis?.displayName || ""),
    decimals: dateMode ? 0 : 2,
    low_good: false,
    plot_lines: spec.xAxis?.plotLines ?? [],
  };
  const yAxisResult: ScatterplotAxisResult = data?.y_axis ?? {
    mode: "metric",
    label: spec.yAxis.displayNameOverride || spec.yAxis.displayName,
    decimals: 2,
    low_good: false,
    plot_lines: spec.yAxis.plotLines ?? [],
  };

  const aggWord = selectionAggWord(dateMode ? null : spec.xAxis?.metricAgg ?? null, spec.yAxis.metricAgg);
  const selectionLabel = selectionSeriesLabel(spec.scatterplot_builder_misc ?? {}, aggWord);
  const scatterSeries = buildScatterSeries(spec, data?.points ?? [], selectionLabel);
  const trendlineSeries = buildTrendlineSeries(data?.trendlines ?? [], scatterSeries);

  return {
    chart: { type: "scatter", zoomType: "xy", backgroundColor: "transparent" },
    title: { text: "" },
    credits: { enabled: false },
    legend: {
      enabled: spec.display.showLegend !== false,
      verticalAlign: "top",
      align: "center",
      layout: "horizontal",
      itemStyle: { fontSize: "11px", fontWeight: "400", color: "#333" },
      maxHeight: 80,
    },
    // Grey grid lines on both axes, like the chart widget (Highcharts draws
    // horizontal lines by default and no vertical ones).
    xAxis: {
      type: dateMode ? "datetime" : "linear",
      title: { text: axisTitle(xAxisResult) },
      gridLineWidth: 1,
      plotLines: toPlotLines(xAxisResult),
      ...softExtremes(xAxisResult),
    },
    yAxis: {
      title: { text: axisTitle(yAxisResult) },
      gridLineWidth: 1,
      plotLines: toPlotLines(yAxisResult),
      ...softExtremes(yAxisResult),
    },
    tooltip: {
      useHTML: true,
      formatter(this: {
        point?: { x?: number; y?: number; options?: { custom?: ScatterPointCustom } };
      }): string {
        const custom = this.point?.options?.custom;
        if (!custom) return "";
        const lines: string[] = [`<strong>${custom.date}</strong>`];
        if (custom.gameday) lines.push(custom.gameday.toUpperCase());
        if (custom.entityLabel) lines.push(custom.entityLabel);
        if (custom.position && custom.position !== custom.entityLabel) lines.push(custom.position);
        if (!dateMode) {
          lines.push(`${xAxisResult.label}: ${formatAxisValue(this.point?.x ?? 0, custom.xLabel, xAxisResult)}`);
        }
        lines.push(`${yAxisResult.label}: ${formatAxisValue(this.point?.y ?? 0, custom.yLabel, yAxisResult)}`);
        return lines.join("<br/>");
      },
    },
    plotOptions: {
      scatter: {
        stickyTracking: false,
        marker: {
          states: { hover: { enabled: true, lineColor: "rgb(100,100,100)" } },
        },
        states: { hover: { marker: { enabled: false } } },
      },
    },
    series: [...scatterSeries, ...trendlineSeries],
  };
}
